/**
 * Tasting-session state machine -- pure logic, no I/O. Authorization (host-only actions) is a
 * separate 403 check at the route layer (edge's requireHost, mirroring flight.ts); this function
 * only ever answers "is this (state, action) pair a valid transition", so every refusal here is a
 * 409, never a 403 (tasting-sessions.feature's valid/invalid transition tables).
 */

export type TastingSessionStatus = "draft" | "scheduled" | "live" | "ended";
export type TastingSessionAction = "schedule" | "start" | "cancel" | "end";

export type TastingSessionTransitionResult =
  | { readonly ok: true; readonly to: TastingSessionStatus }
  | { readonly ok: false; readonly reason: string };

/** Table-driven, mirroring flight.ts's status-guard convention and friendship.ts's
 * transitionFriendship shape -- every (from, action) not listed here is refused, never silently
 * applied. `ended` is always terminal; `live` is only ever reached via start, never set directly. */
export function transitionTastingSession(
  from: TastingSessionStatus,
  action: TastingSessionAction,
): TastingSessionTransitionResult {
  if (from === "draft" && action === "schedule") return { ok: true, to: "scheduled" };
  // Moving a plan is scheduling again: the session stays scheduled, with a new time.
  if (from === "scheduled" && action === "schedule") return { ok: true, to: "scheduled" };
  if (from === "draft" && action === "start") return { ok: true, to: "live" };
  if (from === "scheduled" && action === "start") return { ok: true, to: "live" };
  if (from === "scheduled" && action === "cancel") return { ok: true, to: "ended" };
  if (from === "live" && action === "end") return { ok: true, to: "ended" };
  return { ok: false, reason: `cannot ${action} from ${from}` };
}

/** How long a planned session waits for its host after its time before it is called off. */
export const PLANNED_SESSION_GRACE_MS = 24 * 60 * 60 * 1000;

/**
 * When a planned session lapses: its time plus the grace. Derived from the time alone, so every
 * device and the edge's sweep name the same instant without a clock of their own to agree on.
 */
export function plannedSessionLapsesAt(scheduledAt: string): Date {
  return new Date(Date.parse(scheduledAt) + PLANNED_SESSION_GRACE_MS);
}

/** Whether a session planned for `scheduledAt` that never started is past its grace at `now`. */
export function plannedSessionLapsed(scheduledAt: string | null, now: Date): boolean {
  return scheduledAt !== null && plannedSessionLapsesAt(scheduledAt).getTime() <= now.getTime();
}
